import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import find from 'lodash/find';
import map from 'lodash/map';
import noop from 'lodash/noop';

import { useAccessToken, useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  DepartmentApiDepartmentBuildSchedulesPostRequest,
  DepartmentApiDepartmentGetRequest,
  DepartmentApiDepartmentIdPutRequest,
  DepartmentApiDepartmentSchedulePeriodsGetRequest,
  PeriodTypeName,
  useApi,
} from '..';
import { useUpdateAssignmentsCache } from './manage-schedule';
import { SchedulerDepartmentCacheKeys } from './util/cache-keys';

const buildSchedule =
  (api: Api) => (body: DepartmentApiDepartmentBuildSchedulesPostRequest) =>
    api.departmentApi.departmentBuildSchedulesPost(body);

export const useBuildSchedule = () => {
  const api = useApi();
  const updateAssignmentsCache = useUpdateAssignmentsCache();
  const { notifySuccess, notifyError } = useSnackBar();
  return useMutation(buildSchedule(api), {
    onSuccess: async () => {
      updateAssignmentsCache();
      notifySuccess('Schedule created successfully');
    },
    onError: async (error) => {
      const message = getApiErrorMessage(error);
      if (!message?.includes('NoOpeningTemplatesFoundException')) {
        notifyError(message);
      } else {
        notifyError(
          'No opening template found for this facility and department combination'
        );
      }
    },
  });
};

const updateDepartment =
  (api: Api) => (body: DepartmentApiDepartmentIdPutRequest) => {
    const { id, updateLaborLevelExtensionDto } = body;
    return api.departmentApi.departmentIdPut({
      id,
      updateLaborLevelExtensionDto,
    });
  };

export const useUpdateDepartment = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updateDepartment(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([SchedulerDepartmentCacheKeys.Departments])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useDepartmentsGet = (
  departmentApiDepartmentGetRequest: DepartmentApiDepartmentGetRequest,
  config?: {
    enabled: boolean;
  }
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [
      SchedulerDepartmentCacheKeys.Departments,
      departmentApiDepartmentGetRequest.facilityId,
      departmentApiDepartmentGetRequest.filteredByOpeningTemplates,
    ],
    queryFn: () => {
      return api.departmentApi
        .departmentGet(departmentApiDepartmentGetRequest)
        .then((response) => response?.data?.data ?? []);
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled:
      !isMultiFacility ||
      (isMultiFacility &&
        Boolean(departmentApiDepartmentGetRequest.facilityId)) ||
      config?.enabled,
  });
};
export type DepartmentResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useDepartmentsGet>['data']>
>;

export const useDepartmentOptionsGet = (
  departmentApiDepartmentGetRequest: DepartmentApiDepartmentGetRequest
) => {
  const {
    data: departments = [],
    isSuccess,
    isLoading,
  } = useDepartmentsGet(departmentApiDepartmentGetRequest);
  const options = useMemo(
    () =>
      map(departments, (item) => ({
        id: item.departmentId,
        label: item.name,
        value: item.departmentId,
      })),
    [departments]
  );
  return { options, isLoading, isSuccess };
};

export const useSchedulerPeriodNumberOfWeeks = ({
  facilityId,
  departmentId,
}: {
  facilityId?: number;
  departmentId: number;
}) => {
  const { data: departments = [] } = useDepartmentsGet({
    facilityId,
    filteredByOpeningTemplates: true,
  });
  const schedulerPeriodNumberOfWeeks = useMemo(() => {
    const department = find(departments, { departmentId });
    const periodTypeName = department?.periodTypeName as PeriodTypeName;
    switch (periodTypeName) {
      case PeriodTypeName.TwoWeeks:
        return 2;
      case PeriodTypeName.FourWeeks:
        return 4;
      case PeriodTypeName.OneMonth:
        return 5;
      default:
        return 2;
    }
  }, [departmentId, departments]);
  return { schedulerPeriodNumberOfWeeks };
};

export const useSchedulePeriods = (
  body: DepartmentApiDepartmentSchedulePeriodsGetRequest
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [SchedulerDepartmentCacheKeys.SchedulerPeriods, body],
    queryFn: () =>
      api.departmentApi
        .departmentSchedulePeriodsGet(body)
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled:
      ((isMultiFacility && Boolean(body.facilityId)) || !isMultiFacility) &&
      Boolean(body.departmentId),
  });
};

export const useSchedulePeriodsV2 = (
  departmentId: number,
  facilityId?: number,
  forBuildSchedules?: boolean
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [
      SchedulerDepartmentCacheKeys.SchedulerPeriods,
      departmentId,
      facilityId,
      forBuildSchedules,
    ],
    queryFn: () =>
      api.departmentApi
        .departmentV2SchedulePeriodsGet({
          departmentId,
          facilityId,
        })
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled:
      ((isMultiFacility && Boolean(facilityId)) || !isMultiFacility) &&
      Boolean(departmentId),
  });
};
